/*
  Dhrohar product page helpers. Small custom elements on top of Dawn's product-info / product-form.
  - <dhrohar-variant-sync>: after Dawn's variant change (PUB_SUB_EVENTS.variantChange), copies every
    [data-dhrohar-sync] element from Dawn's own re-rendered section HTML, so variant metafield overrides
    (contents, pieces, dimensions) update. Open accordions stay open.
  - <dhrohar-sticky-add>: mobile bar shown once the main Add to bag button has scrolled out of view.
    It clicks Dawn's real submit button, so errors, loading and the cart drawer stay native.
  - <dhrohar-delivery-check>: 6-digit PIN validation and merchant PIN list match. Never invents dates or coverage.
    Rendered only when the merchant PIN list is set; stays hidden if the list has no valid entries.
  - Size guide: opens the native <dialog>, traps focus, closes on Escape/backdrop and returns focus.
  Safe to re-run on theme editor section reloads: each element cleans up in disconnectedCallback.
*/
(() => {
  const sectionIdOf = (productInfo) =>
    productInfo ? productInfo.dataset.originalSection || productInfo.dataset.section : null;

  /* ---------- variant facts sync ---------- */
  if (!customElements.get('dhrohar-variant-sync')) {
    customElements.define(
      'dhrohar-variant-sync',
      class DhroharVariantSync extends HTMLElement {
        connectedCallback() {
          this.productInfo = this.closest('product-info');
          if (!this.productInfo || typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => this.onVariantChange(event));
        }

        disconnectedCallback() {
          this.unsubscribe?.();
        }

        onVariantChange(event) {
          const data = event && event.data;
          if (!data || !data.html || !this.productInfo.isConnected) return;
          if (data.sectionId !== sectionIdOf(this.productInfo)) return;

          this.productInfo.querySelectorAll('[data-dhrohar-sync]').forEach((current) => {
            const key = current.getAttribute('data-dhrohar-sync');
            const incoming = data.html.querySelector(`[data-dhrohar-sync="${CSS.escape(key)}"]`);
            if (!incoming) return;
            const fresh = document.importNode(incoming, true);
            current.querySelectorAll('details[id]').forEach((details) => {
              const match = fresh.querySelector(`details#${CSS.escape(details.id)}`);
              if (match) match.open = details.open;
            });
            current.replaceWith(fresh);
          });

          // The wishlist heart (<dhrohar-wishlist-button>, assets/dhrohar-wishlist.js) follows variant changes itself.
          this.productInfo.querySelector('dhrohar-sticky-add')?.syncFromMainButton();
        }
      }
    );
  }

  /* ---------- mobile sticky add bar ---------- */
  if (!customElements.get('dhrohar-sticky-add')) {
    customElements.define(
      'dhrohar-sticky-add',
      class DhroharStickyAdd extends HTMLElement {
        connectedCallback() {
          this.productInfo = this.closest('product-info');
          this.button = this.querySelector('.dhrohar-sticky-add__button');
          if (!this.productInfo || !this.button || this.closest('quick-add-modal')) return;

          this.mainButton = this.productInfo.querySelector(
            `#ProductSubmitButton-${this.dataset.sectionId}, product-form [type="submit"]`
          );
          if (!this.mainButton) return;

          this.onClick = () => {
            const main = this.currentMainButton();
            if (!main || main.disabled || main.getAttribute('aria-disabled') === 'true') return;
            main.click();
          };
          this.button.addEventListener('click', this.onClick);

          this.mutation = new MutationObserver(() => this.syncFromMainButton());
          this.mutation.observe(this.mainButton, {
            attributes: true,
            attributeFilter: ['disabled', 'aria-disabled', 'class'],
            childList: true,
            subtree: true,
            characterData: true,
          });

          this.observer = new IntersectionObserver(([entry]) => {
            const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            this.setVisible(show);
          });
          this.observer.observe(this.mainButton);
          // A fast jump (fling, anchor, restored scroll) can move the button from below the fold to above it
          // without ever intersecting, so IntersectionObserver alone would not fire. Re-check on scroll (rAF-throttled).
          this.onScroll = () => {
            if (this.scrollFrame) return;
            this.scrollFrame = setTimeout(() => {
              this.scrollFrame = null;
              const main = this.currentMainButton();
              if (main) this.setVisible(main.getBoundingClientRect().bottom < 0);
            }, 50);
          };
          window.addEventListener('scroll', this.onScroll, { passive: true });
          this.syncFromMainButton();
        }

        disconnectedCallback() {
          this.observer?.disconnect();
          if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
          if (this.scrollFrame) clearTimeout(this.scrollFrame);
          this.mutation?.disconnect();
          this.button?.removeEventListener('click', this.onClick);
          this.setVisible(false);
        }

        currentMainButton() {
          if (this.mainButton && this.mainButton.isConnected) return this.mainButton;
          return this.productInfo?.querySelector('product-form [type="submit"]') || null;
        }

        setVisible(show) {
          this.classList.toggle('is-visible', show);
          this.setAttribute('aria-hidden', String(!show));
          if (this.button) this.button.tabIndex = show ? 0 : -1;
          document.body.classList.toggle('dhrohar-has-sticky-add', show);
        }

        syncFromMainButton() {
          const main = this.currentMainButton();
          if (!main || !this.button) return;
          const disabled = main.disabled || main.getAttribute('aria-disabled') === 'true';
          this.button.disabled = disabled;
          this.button.classList.toggle('loading', main.classList.contains('loading'));
          const label = main.querySelector('span:not(.hidden):not(.loading__spinner)') || main.querySelector('span');
          const text = label ? label.textContent.trim() : '';
          if (text) this.button.textContent = text;
        }
      }
    );
  }

  /* ---------- PIN code check ---------- */
  if (!customElements.get('dhrohar-delivery-check')) {
    customElements.define(
      'dhrohar-delivery-check',
      class DhroharDeliveryCheck extends HTMLElement {
        connectedCallback() {
          this.form = this.querySelector('form');
          this.input = this.querySelector('input[name="pin"]');
          this.result = this.querySelector('.dhrohar-delivery__result');
          if (!this.form || !this.input || !this.result) return;
          this.prefixes = (this.dataset.prefixes || '')
            .split(/[\s,;]+/)
            .map((value) => value.trim())
            .filter((value) => /^\d{1,6}$/.test(value));
          // 0.5: without a usable PIN list the check could only say "shown at checkout", so it stays hidden
          // (the Liquid snippet already leaves it out when the setting is blank).
          if (!this.prefixes.length) return;
          this.onSubmit = (event) => {
            event.preventDefault();
            this.check();
          };
          this.onInput = () => {
            const digits = this.input.value.replace(/\D/g, '').slice(0, 6);
            if (digits !== this.input.value) this.input.value = digits;
          };
          this.form.addEventListener('submit', this.onSubmit);
          this.input.addEventListener('input', this.onInput);
          this.hidden = false;
        }

        disconnectedCallback() {
          this.form?.removeEventListener('submit', this.onSubmit);
          this.input?.removeEventListener('input', this.onInput);
        }

        check() {
          const pin = this.input.value.trim();
          this.result.classList.remove('is-error', 'is-ok');
          if (!/^[1-9]\d{5}$/.test(pin)) {
            this.input.setAttribute('aria-invalid', 'true');
            this.result.classList.add('is-error');
            this.result.textContent = this.dataset.invalid || '';
            this.input.focus();
            return;
          }
          this.input.removeAttribute('aria-invalid');
          if (!this.prefixes.length) {
            this.result.textContent = this.dataset.neutral || '';
            return;
          }
          const match = this.prefixes.some((prefix) => pin.startsWith(prefix));
          if (match) this.result.classList.add('is-ok');
          this.result.textContent = (match ? this.dataset.available : this.dataset.unavailable) || '';
        }
      }
    );
  }

  /* ---------- size guide dialog ---------- */
  if (!window.dhroharSizeGuideReady) {
    window.dhroharSizeGuideReady = true;
    const focusable =
      'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

    const openDialog = (dialog, opener) => {
      if (!dialog || dialog.open || typeof dialog.showModal !== 'function') return;
      dialog.dhroharOpener = opener;
      dialog.showModal();
      document.body.classList.add('overflow-hidden');
      (dialog.querySelector('[data-dhrohar-dialog-close]') || dialog).focus();
    };

    document.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-dhrohar-size-guide]');
      if (opener) {
        openDialog(document.getElementById(opener.getAttribute('data-dhrohar-size-guide')), opener);
        return;
      }
      const dialog = event.target.closest('dialog.dhrohar-dialog');
      if (!dialog) return;
      if (event.target.closest('[data-dhrohar-dialog-close]') || event.target === dialog) dialog.close();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab') return;
      const dialog = event.target.closest && event.target.closest('dialog.dhrohar-dialog[open]');
      if (!dialog) return;
      const items = Array.from(dialog.querySelectorAll(focusable)).filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    document.addEventListener(
      'close',
      (event) => {
        const dialog = event.target;
        if (!(dialog instanceof HTMLDialogElement) || !dialog.classList.contains('dhrohar-dialog')) return;
        document.body.classList.remove('overflow-hidden');
        const opener = dialog.dhroharOpener;
        dialog.dhroharOpener = null;
        if (opener && opener.isConnected) {
          opener.focus();
        } else if (opener) {
          const replacement = document.querySelector(`[data-dhrohar-size-guide="${CSS.escape(dialog.id)}"]`);
          replacement?.focus();
        }
      },
      true
    );
  }
})();
